import React, {useContext, useEffect, useState} from "react";
import {
  Fade,
  Stack,
  CircularProgress,
  Typography,
  Box,
  Alert, Toolbar, IconButton, Tooltip, Button,
  Select,
  MenuItem,
  Divider, TextField,
  Dialog,
  DialogContent,
  DialogActions
} from "@mui/material";
import {v4 as uuidv4} from "uuid"
import {useAtomValue, useSetAtom} from "jotai";
import {ChatDrawerAtom} from "../../../pages/project/atoms";
import {DebugIdAtom} from "../../../pages/project/dashboard/atoms";
import {SignInContext, TOKEN} from "../../../context/SignInContext";
import {ConversationRepository} from "../../../repositories/ConversationRepository";
import {
  SupervisionConversationHistory
} from "../../../pages/project/supervision-page/components/supervision-conversation-history";
import {IconBug} from "@tabler/icons-react";
import {grey} from "@mui/material/colors";
import moment from "moment/moment";

export const ChatDrawer = ({project, conversationType = "CHAT_BOT"}) => {
  const {currentUser} = useContext(SignInContext);
  const [showAnimation, setShowAnimation] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [conversation, setConversation] = useState(null);
  const [showDebug, setShowDebug] = useState(false);
  const [sender, setSender] = useState("")
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogSender, setDialogSender] = useState("");
  const setDebug = useSetAtom(DebugIdAtom);
  const chatDrawer = useAtomValue(ChatDrawerAtom);

  useEffect(() => {
    if (currentUser) {
      const senderLS = localStorage.getItem("sender");
      const sender = senderLS || "";
      setSender(sender)
      const debug = localStorage.getItem("showDebug");
      setShowDebug(debug ? JSON.parse(debug) || false : false);
    }
  }, [currentUser, project?.phonebook]);

  useEffect(() => {
    const senderLS = localStorage.getItem("sender");
    if (project?.id && chatDrawer?.open && senderLS) {
      const debugId = uuidv4();
      localStorage.setItem("debug", debugId);
      setDebug(debugId);
      const chatui_api = project?.configuration?.chatui_api ? `${project?.configuration.chatui_api}/${project?.id}?entryNode=${chatDrawer?.startNode}&jwt=${localStorage.getItem(TOKEN)}` : `${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/api/apiCurrentProject/${project?.id}?debug=${debugId}&entryNode=${chatDrawer?.startNode}`
      console.log("Start conversation", chatDrawer, chatui_api)
      handleCreateConversation(chatui_api, senderLS);
    }
  }, [project?.id, chatDrawer?.open, chatDrawer?.startNode]);


  const handleDialogSenderChange = (event) => {
    setDialogSender(event.target.value);
  };

  const handleOpenDialog = () => {
    setDialogSender(sender);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDialogSender("");
  };

  const handleCreateConversation = (json = null, senderNumber) => {
    setShowAnimation(true);
    setHasError(false);
    const username = currentUser?.username;
    const senderToUse = senderNumber || sender || (project?.vdnManager ? project?.vdnManager[username] || -999 : -999);

    ConversationRepository.createConversation({
      flowUid: project?.id,
      sender: senderToUse,
      conversationType: conversationType,
      json: json,
    })
      .then((response) => {
        setSender(senderToUse);
        localStorage.setItem("sender", senderToUse)
        setShowAnimation(false);
        if (response?.data?.id && response?.data?.status !== "FAILED") {
          setTimeout(() => {
            setConversation(response.data);
          }, 730)
        } else {
          setHasError(true)
        }
      })
      .catch(error => {
        console.log(error)
        setHasError(true);
      })
  }

  let content = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "300px",
        my: 'auto',
        gap: 2
      }}
    >
      <CircularProgress/>
      <Typography variant="body2" color="text.secondary" textAlign="center">
        This will take some time while we start conversation
      </Typography>
    </Box>
  )
  if (!showAnimation && !conversation?.id) {
    content = (
      <Stack direction={"column"} gap={1} sx={{ p: 3, my: "auto" }}>
        <Typography variant="h6" gutterBottom>
          Phone Number
        </Typography>
        {
          project?.phonebook ?
            <>
              <Select
                // labelId="sender-select-label"
                id="sender-select"
                value={sender}
                // label="Choose a sender"
                onChange={(event) => {
                  const selectedSender = event.target.value;
                  setSender(selectedSender);
                  localStorage.setItem("sender", selectedSender);
                }}
              >
                {project?.phonebook && Object.entries(project.phonebook).map(([key, value]) => (
                  <MenuItem key={key} value={key}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
              <Divider>OR</Divider>
            </>
            : null
        }
        <TextField
          placeholder={project?.phonebook ? "Or enter your (test) number" : "Enter your (test) phonenumber"}
          value={sender}
          onChange={(event) => setSender(event.target.value)}
          onBlur={(event) => localStorage.setItem("sender", event.target.value)}
          helperText={"Enter valid or invalid phone number to test your scenario"}
        />


        <Button
          disabled={!project?.id || !sender}
          size={"large"}
          variant={"primary"}
          sx={{borderRadius: 2, mt: 3}}
          onClick={() => {
            localStorage.setItem("sender", sender);
            if (project?.id && chatDrawer?.open) {
              const debugId = uuidv4();
              localStorage.setItem("debug", debugId);
              setDebug(debugId);
              const url = `${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/api/apiCurrentProject/${project?.id}?debug=${debugId}&entryNode=${chatDrawer?.startNode}`
              handleCreateConversation(url);
            }
          }}
        >
          Start Conversation
        </Button>
      </Stack>
    )
  } else if (hasError) {
    content = <Box sx={{p: 2}}>
      <Alert severity="error">
        An error occurred. The conversation failed to start. Please try later, or check if the flow is correct.
      </Alert>
    </Box>
  } else if (!showAnimation && conversation?.id) {
    content = (
      <>
        <Toolbar sx={{px: 0, justifyContent: "space-between"}}>
          <Stack direction={"row"} gap={1}>
            {/*<Box><IconButton onClick={onClose}><IconChevronLeft/></IconButton></Box>*/}
            <Box>
              <Tooltip title={`${conversation?.sender}`}>
                <Typography sx={{cursor: "pointer"}} fontWeight={500} onClick={handleOpenDialog}>
                  {conversation?.id}. {conversation?.sender}
                </Typography>
              </Tooltip>
              <Typography fontWeight={400} color={grey[500]}
                          fontSize={"0.8em"}>
                {moment(conversation?.dateCreated)?.format("DD MMM YYYY HH:mm")}
              </Typography>
            </Box>
          </Stack>

          <Stack direction={"row"} alignItems={"center"} gap={1}>
            <Box>
              <Tooltip title={"Show Debug Messages"}>
                <IconButton
                  size={"small"}
                  color={showDebug ? "success" : "default"}
                  onClick={() => {
                    setShowDebug(prev => {
                      localStorage.setItem("showDebug", JSON.stringify(!prev))
                      return !prev;
                    })
                  }}
                >
                  <IconBug size={22}/>
                </IconButton>
              </Tooltip>
              <Button
                disabled={!project?.id || !sender}
                size={"small"}
                variant={"primary"}
                sx={{borderRadius: 2}}
                onClick={() => {
                  if (project?.id && chatDrawer?.open) {
                    const debugId = uuidv4();
                    localStorage.setItem("debug", debugId);
                    setDebug(debugId);
                    const url = `${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/api/apiCurrentProject/${project?.id}?debug=${debugId}&entryNode=${chatDrawer?.startNode}`
                    handleCreateConversation(url);
                  }
                }}
              >
                Start Again
              </Button>
            </Box>
          </Stack>
        </Toolbar>
        <Stack direction={"column"} sx={{flex: 1, maxHeight: "calc(100dvh - 125px)"}}>
          <SupervisionConversationHistory
            selectedConversation={conversation}
            setSelectedConversation={setConversation}
            project={project}
            debug={showDebug}
          />
        </Stack>
      </>
    )
  }

  return (
    <>
      <Fade in={chatDrawer?.open} timeout={500}>
        <Stack direction={"column"} gap={1} sx={{
          display: chatDrawer?.open ? "flex" : "none",
          minWidth: "400px",
          maxWidth: "460px",
          maxHeight: "calc(100dvh - 50px)",
          width: "100% !important",
          bgcolor: "#FFF",
          overflow: "hidden",
          borderLeft: 1,
          borderColor: "divider"
        }}>
          {content}
        </Stack>
      </Fade>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        {/*<DialogTitle>Change Phone Number</DialogTitle>*/}
        <DialogContent>
          <Stack direction={"column"} gap={2} sx={{ pt: 1 }}>
            <Typography variant="h6" gutterBottom>
              Phone Number
            </Typography>
            {
              project?.phonebook ?
                <>
                  <Select
                    id="dialog-sender-select"
                    value={dialogSender}
                    onChange={handleDialogSenderChange}
                    placeholder={"Select from phonebook"}
                    fullWidth
                  >
                    {project?.phonebook && Object.entries(project.phonebook).map(([key, value]) => (
                      <MenuItem key={key} value={key}>
                        {value}
                      </MenuItem>
                    ))}
                  </Select>
                  <Divider>OR</Divider>
                </>
                : null
            }
            <TextField
              placeholder={project?.phonebook ? "Or enter your (test) number" : "Enter your (test) phonenumber"}
              value={dialogSender}
              onChange={handleDialogSenderChange}
              helperText={"Enter valid or invalid phone number to test your scenario"}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{px: 3, pb: 3}}>
          <Button variant="ghost" onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={() => {
              if (project?.id && chatDrawer?.open) {
                const debugId = uuidv4();
                localStorage.setItem("debug", debugId);
                setDebug(debugId);
                const url = `${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/api/apiCurrentProject/${project?.id}?debug=${debugId}&entryNode=${chatDrawer?.startNode}`
                handleCreateConversation(url, dialogSender || sender);
                setDialogOpen(false);
                setDialogSender("");
              }
            }}

            variant="primary"
            disabled={!dialogSender}
          >
            Start New Conversation
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
